/**
 * NEXUS V3 - Direct Agent Tester
 * 
 * Bypass orchestrator pipeline with direct agent-to-agent API calls
 * Focus on deterministic price comparison functionality
 */

import React, { useState } from 'react';
import { Play, Eye, Zap, DollarSign, AlertCircle, CheckCircle } from 'lucide-react';
import { getApiBaseUrl } from '@/services/api';

interface DirectAgentTesterProps {}

interface PriceComparisonData {
  order_id: string;
  price_data: Record<string, any>;
  discrepancies: any[];
  systems_compared: number;
  systems_with_data: number;
}

interface AgentResponse {
  success: boolean;
  agent_type: string;
  order_id: string;
  summary: string;
  confidence: number;
  next_action?: string;
  details?: any;
  error?: string;
}

const DirectAgentTester: React.FC<DirectAgentTesterProps> = () => {
  const [orderId, setOrderId] = useState('DT-REAUTH-005');
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [responses, setResponses] = useState<Record<string, AgentResponse | PriceComparisonData>>({});
  
  const API_BASE = getApiBaseUrl();

  const setAgentLoading = (agent: string, isLoading: boolean) => {
    setLoading(prev => ({ ...prev, [agent]: isLoading }));
  };

  const callDirectMonitor = async () => {
    setAgentLoading('monitor', true);
    try {
      const response = await fetch(`${API_BASE}/direct-agents/monitor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: orderId,
          metadata: { trigger: 'direct_test', change_type: 'price_change' }
        })
      });
      
      const data = await response.json();
      setResponses(prev => ({ ...prev, monitor: data }));
    } catch (error: any) {
      console.error('Monitor Agent call failed:', error);
      setResponses(prev => ({ ...prev, monitor: { success: false, error: error.message, agent_type: 'monitor', order_id: orderId, summary: 'Failed', confidence: 0 } }));
    } finally {
      setAgentLoading('monitor', false);
    }
  };

  const callDirectVerify = async () => {
    setAgentLoading('verify', true);
    try {
      const response = await fetch(`${API_BASE}/direct-agents/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: orderId,
          metadata: { trigger: 'direct_test' }
        })
      });
      
      const data = await response.json();
      setResponses(prev => ({ ...prev, verify: data }));
    } catch (error: any) {
      console.error('Verify Agent call failed:', error);
      setResponses(prev => ({ ...prev, verify: { success: false, error: error.message, agent_type: 'verify', order_id: orderId, summary: 'Failed', confidence: 0 } }));
    } finally {
      setAgentLoading('verify', false);
    }
  };

  const callDirectExecute = async () => {
    setAgentLoading('execute', true);
    try {
      const response = await fetch(`${API_BASE}/direct-agents/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: orderId,
          metadata: { trigger: 'direct_test' }
        })
      });
      
      const data = await response.json();
      setResponses(prev => ({ ...prev, execute: data }));
    } catch (error: any) {
      console.error('Execute Agent call failed:', error);
      setResponses(prev => ({ ...prev, execute: { success: false, error: error.message, agent_type: 'execute', order_id: orderId, summary: 'Failed', confidence: 0 } }));
    } finally {
      setAgentLoading('execute', false);
    }
  };

  const getPriceComparison = async () => {
    setAgentLoading('price', true);
    try {
      const response = await fetch(`${API_BASE}/direct-agents/price-comparison/${orderId}`);
      const data = await response.json();
      setResponses(prev => ({ ...prev, price: data }));
    } catch (error: any) {
      console.error('Price comparison failed:', error);
      setResponses(prev => ({ ...prev, price: { order_id: orderId, error: error.message, price_data: {}, discrepancies: [], systems_compared: 0, systems_with_data: 0 } }));
    } finally {
      setAgentLoading('price', false);
    }
  };

  const renderAgentResponse = (key: string, response: AgentResponse) => {
    const iconMap = {
      monitor: <Eye className="h-5 w-5" />,
      verify: <CheckCircle className="h-5 w-5" />,
      execute: <Zap className="h-5 w-5" />
    };

    return (
      <div key={key} className={`p-4 rounded-lg border ${response.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
        <div className="flex items-center gap-2 mb-2">
          {iconMap[key as keyof typeof iconMap] || <AlertCircle className="h-5 w-5" />}
          <h3 className="font-semibold capitalize">{response.agent_type} Agent</h3>
          <span className={`px-2 py-1 rounded-full text-xs ${response.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {response.success ? 'Success' : 'Failed'}
          </span>
        </div>

        <div className="space-y-2 text-sm">
          <div><strong>Summary:</strong> {response.summary}</div>
          <div><strong>Confidence:</strong> {(response.confidence * 100).toFixed(1)}%</div>
          {response.next_action && <div><strong>Next Action:</strong> {response.next_action}</div>}
          {response.error && <div className="text-red-600"><strong>Error:</strong> {response.error}</div>}
          
          {response.details && (
            <details className="mt-2">
              <summary className="cursor-pointer font-medium">View Details</summary>
              <pre className="text-xs bg-gray-100 p-2 rounded mt-2 overflow-x-auto">
                {JSON.stringify(response.details, null, 2)}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  };

  const renderPriceComparison = (data: PriceComparisonData) => {
    return (
      <div className="p-4 rounded-lg border bg-blue-50 border-blue-200">
        <div className="flex items-center gap-2 mb-3">
          <DollarSign className="h-5 w-5" />
          <h3 className="font-semibold">Price Comparison</h3>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
          <div><strong>Systems Compared:</strong> {data.systems_compared}</div>
          <div><strong>Systems with Data:</strong> {data.systems_with_data}</div>
        </div>

        {/* Price Data Table */}
        <div className="overflow-x-auto mb-4">
          <table className="min-w-full text-xs border border-gray-200 rounded">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left border-b font-medium">System</th>
                <th className="px-3 py-2 text-left border-b font-medium">Found</th>
                <th className="px-3 py-2 text-left border-b font-medium">Total Amount</th>
                <th className="px-3 py-2 text-left border-b font-medium">Tax Amount</th>
                <th className="px-3 py-2 text-left border-b font-medium">Subtotal</th>
                <th className="px-3 py-2 text-left border-b font-medium">Items</th>
                <th className="px-3 py-2 text-left border-b font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(data.price_data).map(([system, systemData]: [string, any]) => (
                <tr key={system} className="border-b">
                  <td className="px-3 py-2 font-medium">{system}</td>
                  <td className="px-3 py-2">
                    {systemData.found ? (
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">✅ Found</span>
                    ) : (
                      <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">❌ Not Found</span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono">
                    {systemData.found ? `$${systemData.total_amount?.toFixed(2) || '0.00'}` : 'N/A'}
                  </td>
                  <td className="px-3 py-2 font-mono">
                    {systemData.found ? `$${systemData.tax_amount?.toFixed(2) || '0.00'}` : 'N/A'}
                  </td>
                  <td className="px-3 py-2 font-mono">
                    {systemData.found ? `$${systemData.subtotal?.toFixed(2) || '0.00'}` : 'N/A'}
                  </td>
                  <td className="px-3 py-2">
                    {systemData.found ? systemData.item_count || 0 : 'N/A'}
                  </td>
                  <td className="px-3 py-2">
                    {systemData.found ? systemData.status || 'unknown' : 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Discrepancies */}
        {data.discrepancies && data.discrepancies.length > 0 && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
            <h4 className="font-semibold text-yellow-800 text-sm mb-2">⚠️ Discrepancies Found:</h4>
            {data.discrepancies.map((discrepancy, idx) => (
              <div key={idx} className="text-yellow-700 text-xs">
                <strong>{discrepancy.field}:</strong> {JSON.stringify(discrepancy.systems)} (Severity: {discrepancy.severity})
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6">
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg shadow-lg p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">🚀 Direct Agent Tester</h1>
        <p className="text-purple-100">Bypass orchestrator pipeline - Direct agent-to-agent API calls</p>
      </div>

      {/* Order Input */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Order ID</label>
            <input
              type="text"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter order ID..."
            />
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <button
          onClick={callDirectMonitor}
          disabled={loading.monitor}
          className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Eye className="h-4 w-4" />
          {loading.monitor ? 'Running...' : 'Monitor Agent'}
        </button>

        <button
          onClick={callDirectVerify}
          disabled={loading.verify}
          className="flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <CheckCircle className="h-4 w-4" />
          {loading.verify ? 'Running...' : 'Verify Agent'}
        </button>

        <button
          onClick={callDirectExecute}
          disabled={loading.execute}
          className="flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Zap className="h-4 w-4" />
          {loading.execute ? 'Running...' : 'Execute Agent'}
        </button>

        <button
          onClick={getPriceComparison}
          disabled={loading.price}
          className="flex items-center justify-center gap-2 px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <DollarSign className="h-4 w-4" />
          {loading.price ? 'Loading...' : 'Price Compare'}
        </button>
      </div>

      {/* Results */}
      <div className="space-y-4">
        {Object.entries(responses).map(([key, response]) => {
          if (key === 'price' && 'price_data' in response) {
            return <div key={key}>{renderPriceComparison(response as PriceComparisonData)}</div>;
          } else {
            return renderAgentResponse(key, response as AgentResponse);
          }
        })}
      </div>
    </div>
  );
};

export default DirectAgentTester;
